<script setup lang="ts">
import type { Channel } from '~/types'

import { momentFormat } from '~/utils/filters'
import { useI18n } from 'vue-i18n'
import { useStore } from '~/store'
import { computed } from 'vue'

import { to } from '~/routing'

import moment from 'moment'

import PlayButton from '~/components/audio/PlayButton.vue'
import Card from '~/ui/Card.vue'
import Spacer from '~/ui/Spacer.vue'
import ActorLink from '~/components/common/ActorLink.vue'

interface Props {
  object: Channel
}

const props = defineProps<Props>()
const store = useStore()

const imageUrl = computed(() => props.object.artist?.cover
  ? store.getters['instance/absoluteUrl'](props.object.artist.cover.urls.medium_square_crop)
  : null
)

const urlId = computed(() => props.object.actor?.is_local
  ? props.object.actor.preferred_username
  : props.object.actor
    ? props.object.actor.full_username
    : props.object.uuid
)

const { t } = useI18n()
const updatedTitle = computed(() => {
  const date = momentFormat(new Date(props.object.artist?.modification_date ?? '1970-01-01'))
  return t('components.audio.ChannelCard.title', { date })
})

// TODO (wvffle): Use time ago
const updatedAgo = computed(() => moment(props.object.artist?.modification_date).fromNow())
</script>

<template>
  <Card
    :title="object.artist?.name"
    :tags="object.artist?.tags ?? []"
    v-bind="to({name: 'channels.detail', params: {id: urlId}})"
    solid
    small
  >
    <template #topright>
      <PlayButton
        icon-only
        :artist="object.artist"
        :is-playable="true"
      />
    </template>

    <template #image="{ circle }">
      <img
        v-if="imageUrl"
        v-bind="object.artist?.content_category !== 'podcast' ? circle() : undefined"
        v-lazy="imageUrl"
        :alt="object.artist?.name"
        :style="object.artist?.content_category === 'podcast' ? 'width: 100%' : undefined"
      >
      <Icon
        v-else
        :icon="object.artist?.content_category === 'podcast' ? 'bi:mic' : 'bi:person-circle'"
        :class="object.artist?.content_category === 'podcast' ? 'podcast-icon' : 'music-icon'"
      />
    </template>

    <template #default>
      <Spacer :size="8" />
      <ActorLink
        :actor="object.attributed_to"
        discrete
        :truncate-length=15
      />
    </template>

    <template #footer>
    <!-- TODO: Refactor all footers in those specialized cards; better responsive layout handling! Perhaps CSS classes for smarterwrapping behavior, and/or grid layout -->
      <span class="font-small layout-row" style="padding-left: 16px">
      <time
        :datetime="
          object.artist?.modification_date
        "
        :title="updatedTitle"
      >
        {{ updatedAgo }}
      </time>
      <Icon icon="bi:dot" />
      <span
        v-if="object.artist?.content_category === 'podcast'"
      >
        {{ t('components.audio.ChannelCard.meta.episodes', object.artist.tracks_count) }}
      </span>
      <span v-else>
        {{ t('components.audio.ChannelCard.meta.tracks', object.artist?.tracks_count ?? 0) }}
      </span>
      </span>
      <Spacer
        no-size
        grow
      />
      <PlayButton
        dropdown-only
        is-playable
        discrete
        :artist="object.artist"
        :channel="object"
        :account="object.attributed_to"
      />
    </template>
  </Card>
</template>

<style scoped>
.podcast-icon {
  width: 100%;
  height: 100%;
  padding: 48px;
  background-color: var(--color-300);
}

.music-icon {
  width: 100%;
  height: 100%;
  padding: 16px;
}
</style>
